import axios from "axios";
import swalAlert from "@/services/alert/swalServices";
import { jwtDecode } from "jwt-decode";
import router from "@/router";
import store from "@/store";

const API_URL = process.env.VUE_APP_API_URL;

const TOKEN_KEYS = {
  user: "user_token",
  seller: "seller_token",
  rider: "rider_token",
  admin: "admin_token",
};

const LOGIN_ROUTES = {
  user: "login",
  seller: "login_seller",
  rider: "login_rider",
  admin: "login_admin",
};

const logoutFlags = { user: false, seller: false, rider: false, admin: false };

function getToken(authGroup) {
  const specificKey = TOKEN_KEYS[authGroup];
  return (specificKey ? localStorage.getItem(specificKey) : null) || "";
}

function clearAuthForGroup(authGroup) {
  const tokenKey = TOKEN_KEYS[authGroup];
  const roleKey = tokenKey?.replace("_token", "_role");
  try {
    if (tokenKey) localStorage.removeItem(tokenKey);
    if (roleKey) localStorage.removeItem(roleKey);
  } catch (e) {
    console.error("clearAuthForGroup", e);
  }
}

function createForceLogout(authGroup) {
  return async (message) => {
    if (logoutFlags[authGroup]) return;
    logoutFlags[authGroup] = true;

    if (message) {
      await swalAlert.Error(message, "กำลังนำคุณไปที่หน้าเข้าสู่ระบบ...");
    }

    // sync Vuex state + clear localStorage
    try {
      await store.dispatch("forceLogout", { authGroup });
    } catch {
      clearAuthForGroup(authGroup);
    }

    const loginRouteName = LOGIN_ROUTES[authGroup] || "login";
    const currentRoute = router.currentRoute?.value || router.currentRoute;
    const redirectPath = currentRoute?.fullPath;
    router.push({
      name: loginRouteName,
      query: redirectPath ? { redirect: redirectPath } : {},
    });
    logoutFlags[authGroup] = false;
  };
}

function createApiInstance(authGroup) {
  const instance = axios.create({
    baseURL: API_URL,
    headers: { "Content-Type": "application/json" },
  });

  const forceLogout = createForceLogout(authGroup);

  instance.interceptors.request.use(
    (config) => {
      if (config.skipAuth) return config;

      const token = getToken(authGroup);
      if (token) {
        try {
          const decoded = jwtDecode(token);
          const now = Math.floor(Date.now() / 1000);
          if (decoded.exp && decoded.exp < now) {
            return forceLogout("เซสชันหมดอายุ").then(() =>
              Promise.reject(new Error("Token expired"))
            );
          }
          config.headers.Authorization = `Bearer ${token}`;
          const userId = decoded.sub || decoded.userId || decoded.id;
          if (userId) config.headers["X-User-Id"] = userId;
        } catch (e) {
          console.error("Invalid token:", e);
          return forceLogout("Token ไม่ถูกต้อง").then(() =>
            Promise.reject(new Error("Invalid token"))
          );
        }
      }

      if (config.data instanceof FormData) {
        delete config.headers["Content-Type"];
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const data = error.response?.data;
      const status = error.response?.status;
      const currentRoute = router.currentRoute?.value || router.currentRoute;
      const isLoginRequest = error.config?.skipAuth === true;
      const isFormPage =
        currentRoute?.name === "job_recruitment_form" ||
        currentRoute?.name === "training_form";

      // force logout เฉพาะเมื่อ group นี้มี token อยู่จริง (token หมดอายุ / invalid)
      // ถ้าไม่มี token → แค่ reject ปกติ ไม่ redirect ข้าม group
      const hadToken = !!getToken(authGroup);

      if (
        (status === 401 || data?.message === "Invalid token") &&
        !isFormPage &&
        !isLoginRequest &&
        hadToken
      ) {
        await forceLogout("กรุณาเข้าสู่ระบบใหม่");
        return Promise.reject(error);
      }
      return Promise.reject(error);
    }
  );

  return instance;
}

export const userApi = createApiInstance("user");
export const sellerApi = createApiInstance("seller");
export const riderApi = createApiInstance("rider");
export const adminApi = createApiInstance("admin");

export default userApi;
